import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { formatEuro } from "./helpers";
import { StepHeader } from "./ProgressBar";

export default function StepGegevens({
  first,
  setFirst,
  last,
  setLast,
  email,
  setEmail,
  phone,
  setPhone,
  gdpr,
  setGdpr,
  withdrawalWaiver,
  setWithdrawalWaiver,
  wantBizRecs,
  setWantBizRecs,
  discountCode,
  setDiscountCode,
  discountInfo,
  setDiscountInfo,
  validatingCode,
  validateDiscount,
  finalAmountCents,
  baseAmountCents,
}) {
  return (
    <div className="max-w-xl mx-auto space-y-8 animate-[fade-in-up_500ms_ease-out_both]">
      <StepHeader sub="Je e-mailadres is enkel voor je rapport. Geen spam, beloofd.">
        Waar sturen we je rapport naartoe?
      </StepHeader>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Voornaam</Label>
          <Input
            data-testid="input-firstname"
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            className="h-12 rounded-oc-btn border-oc-border bg-white"
          />
        </div>
        <div className="space-y-2">
          <Label>Familienaam</Label>
          <Input
            data-testid="input-lastname"
            value={last}
            onChange={(e) => setLast(e.target.value)}
            className="h-12 rounded-oc-btn border-oc-border bg-white"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>E-mailadres</Label>
        <Input
          data-testid="input-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12 rounded-oc-btn border-oc-border bg-white"
        />
      </div>
      <div className="space-y-2">
        <Label>Telefoonnummer (optioneel)</Label>
        <Input
          data-testid="input-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="h-12 rounded-oc-btn border-oc-border bg-white"
        />
      </div>

      {/* Discount code — unique per-customer referral codes */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          Kortingscode{" "}
          <span className="text-oc-textSecondary text-[12px] font-normal">(optioneel)</span>
        </Label>
        <div className="flex gap-2">
          <Input
            data-testid="input-discount-code"
            value={discountCode}
            onChange={(e) => {
              setDiscountCode(e.target.value.toUpperCase());
              setDiscountInfo(null);
            }}
            placeholder="OE-XXXXXXXX"
            className="h-12 rounded-oc-btn border-oc-border bg-white font-mono uppercase tracking-wider"
          />
          <Button
            type="button"
            data-testid="validate-discount-btn"
            onClick={validateDiscount}
            disabled={!discountCode.trim() || validatingCode}
            className="h-12 rounded-oc-btn bg-oc-secondary hover:bg-oc-secondary/90 text-white px-5"
          >
            {validatingCode ? "..." : "Valideren"}
          </Button>
        </div>
        {discountInfo?.valid && (
          <div
            data-testid="discount-valid"
            className="flex items-center gap-2 text-oc-fair text-[13px] font-semibold p-3 bg-oc-fair/10 rounded-oc-btn border border-oc-fair/20"
          >
            <CheckCircle2 className="w-4 h-4" />
            {discountInfo.percent_off}% korting toegepast — je betaalt nu € {formatEuro(finalAmountCents)} i.p.v. €{" "}
            {formatEuro(baseAmountCents)}
          </div>
        )}
        {discountInfo?.valid === false && (
          <p className="text-oc-excessive text-[13px]" data-testid="discount-invalid">
            {discountInfo.message}
          </p>
        )}
      </div>

      <label
        data-testid="gdpr-checkbox-wrap"
        className="flex items-start gap-3 p-5 rounded-oc bg-white border border-oc-border cursor-pointer"
      >
        <Checkbox
          checked={gdpr}
          onCheckedChange={(v) => setGdpr(!!v)}
          className="mt-0.5"
          data-testid="gdpr-checkbox"
        />
        <span className="text-[14px] text-oc-textSecondary leading-relaxed">
          Ik ga akkoord met de{" "}
          <Link to="/algemene-voorwaarden" target="_blank" className="text-oc-secondary underline">
            Algemene Voorwaarden
          </Link>{" "}
          en het{" "}
          <Link to="/privacy-policy" target="_blank" className="text-oc-secondary underline">
            Privacybeleid
          </Link>
          . Ik begrijp dat mijn gegevens en documenten 30 dagen bewaard worden en daarna automatisch en permanent verwijderd worden.
        </span>
      </label>

      <label
        data-testid="withdrawal-waiver-wrap"
        className="flex items-start gap-3 p-5 rounded-oc bg-white border border-oc-border cursor-pointer"
      >
        <Checkbox
          checked={withdrawalWaiver}
          onCheckedChange={(v) => setWithdrawalWaiver(!!v)}
          className="mt-0.5"
          data-testid="withdrawal-waiver-checkbox"
        />
        <span className="text-[14px] text-oc-textSecondary leading-relaxed">
          <strong className="text-oc-primary">Ik begrijp dat de dienst meteen wordt geleverd</strong> en accepteer dat mijn herroepingsrecht daardoor vervalt.
        </span>
      </label>

      <label
        data-testid="biz-recs-wrap"
        className="flex items-start gap-3 p-5 rounded-oc bg-oc-subtle/50 border border-oc-border cursor-pointer"
      >
        <Checkbox
          checked={wantBizRecs}
          onCheckedChange={(v) => setWantBizRecs(!!v)}
          className="mt-0.5"
          data-testid="biz-recs-checkbox"
        />
        <span className="text-[14px] text-oc-textSecondary leading-relaxed">
          <strong className="text-oc-primary">Ja, ik wil aanbevelingen ontvangen</strong> van 2 à 3 betrouwbare bedrijven in mijn regio (per e-mail, vrijblijvend).{" "}
          <span className="text-oc-textSecondary text-[12px] italic">— standaard uitgevinkt, je beslist zelf.</span>
        </span>
      </label>

      <div className="flex items-start gap-3 p-5 rounded-oc bg-oc-subtle border border-oc-border text-[14px] text-oc-textPrimary">
        <ShieldCheck className="w-5 h-5 text-oc-secondary shrink-0 mt-0.5" />
        <span>
          Je gegevens zijn veilig. We bewaren je documenten max. 30 dagen op beveiligde servers en delen ze nooit met derden.
        </span>
      </div>

      <div
        className="flex items-start gap-3 p-5 rounded-oc bg-[rgba(234,88,12,0.05)] border border-[rgba(234,88,12,0.2)] text-[14px] text-oc-textPrimary"
        data-testid="b2c-only-notice"
      >
        <span className="font-semibold text-oc-high uppercase tracking-wide text-[12px]">B2C</span>
        <span>
          OfferteExpert is uitsluitend bedoeld voor <strong>particulieren</strong>. Bedrijfsoffertes en zakelijke prijzen vallen buiten onze scope. Door verder te gaan bevestig je dat de offerte voor privégebruik is.
        </span>
      </div>
    </div>
  );
}
